import React, { useState, useEffect } from "react";

import {
  getFulfilmentDetailsByContainer,
  acceptFulfilmentSkus,
  reportDamagedSku,
  recoverShortSku,
} from "../api/fulfilmentApi";

export default function FulfilmentCenterAcceptance() {
  const [manual, setManual] = useState(false);

  // Modes
  const [reportMode, setReportMode] = useState(false);
  const [recoverMode, setRecoverMode] = useState(false);

  const [containerNo, setContainerNo] = useState("");
  const [asn, setAsn] = useState("");
  const [sku, setSku] = useState("");
  const [shipperId, setShipperId] = useState("");
  const [shipperName, setShipperName] = useState("");
  const [productdescription, setproductdescription] = useState("");
  const [damageStatus, setDamageStatus] = useState("Damaged");
  const [shipperEmail, setShipperEmail] = useState("");

  const [skuList, setSkuList] = useState([]);

  // acceptedSkus = only FULLY accepted SKUs
  const [acceptedSkus, setAcceptedSkus] = useState([]);

  const [expected, setExpected] = useState(0);
  const [received, setReceived] = useState(0);
  const [damaged, setDamaged] = useState(0);

  // recovery qty
  const [recoveredQty, setRecoveredQty] = useState(0);
  const [pendingRecovery, setPendingRecovery] = useState(0); // ✅ New state for recoverable pieces

  const [orderStatus, setOrderStatus] = useState("Pending");

  const difference = Math.max(Number(expected) - Number(received), 0);

  /* ===============================
     RESET HELPERS
  =============================== */
  const resetAll = () => {
    setContainerNo("");
    setAsn("");
    setSku("");
    setSkuList([]);
    setAcceptedSkus([]);
    setExpected(0);
    setReceived(0);
    setDamaged(0);
    setRecoveredQty(0);
    setPendingRecovery(0); // ✅
    setShipperId("");
    setShipperName("");
    setOrderStatus("Pending");
    setDamageStatus("Damaged");
    setReportMode(false);
    setRecoverMode(false);
  };

  const resetSkuLevel = () => {
    setSku("");
    setReceived(0);
    setDamaged(0);
    setRecoveredQty(0);
    setExpected(0);
    setPendingRecovery(0); // ✅
  };

  /* ===============================
     AUTO SCAN 
  =============================== */
  useEffect(() => {
    if (manual) return;
    if (!containerNo || containerNo.trim().length < 4) return;

    const t = setTimeout(() => {
      loadDetails(containerNo.trim());
    }, 300);

    return () => clearTimeout(t);
  }, [containerNo, manual]);

  /* ===============================
     LOAD CONTAINER DETAILS
  =============================== */
  const loadDetails = async (cn) => {
    try {
      const res = await getFulfilmentDetailsByContainer(
        cn,
        JSON.parse(localStorage.getItem("user"))?.userId
      );

      const payload = res.data;
      const data = payload?.data || payload;

      if (!data) {
        alert("❌ No ASN linked with this container");
        resetAll();
        return;
      }

      if (data.Status === "NOT_FOUND") {
        alert("❌ Container not mapped to ASN");
        resetAll();
        return;
      }

      if (data.Status === "PENDING") {
        alert("⚠️ Some containers for this ASN are still pending");
        resetAll();
        return;
      }

      if (data.Status === "COMPLETE") {
        setAsn(data.ASNNumber);
        setShipperId(data.ShipperId);
        setShipperName(data.ShipperName);
        setShipperEmail(data.ShipperEmail || "");
        setSkuList(data.skus || []);
        setAcceptedSkus([]);
        setOrderStatus("Select SKU for Acceptance");
        setSku("");
        setExpected(0);
        setReceived(0);
        setDamaged(0);
        setRecoveredQty(0);
        setPendingRecovery(0); // ✅
        return;
      }

      alert("❌ Unexpected response");
      resetAll();
    } catch (err) {
      console.error("Load container error:", err);
      alert("❌ Failed to fetch container details");
      resetAll();
    }
  };

  /* ===============================
     ACCEPT SKU
  =============================== */
  const handleAccept = async () => {
    if (!sku) return alert("⚠️ Select SKU");
    if (received <= 0) return alert("⚠️ Received must be > 0");
    if (received > expected) return alert("❌ Received > Expected");

    try {
      const payload = {
        asn,
        userId: JSON.parse(localStorage.getItem("user"))?.userId,
        skus: [
          {
            SKU: sku,
            Expected: expected,
            Received: received,
            ShipperId: shipperId,
            ShipperName: shipperName,
            ProductDescription: productdescription,
            ShipperEmail: shipperEmail,
          },
        ],
      };

      await acceptFulfilmentSkus(payload);

      const newSkuList = skuList.map((item) =>
        item.SKU === sku
          ? { ...item, Expected: Math.max(Number(item.Expected) - Number(received), 0) }
          : item
      );
      setSkuList(newSkuList);

      const updatedSkuObj = newSkuList.find((x) => x.SKU === sku);
      const remainingExpected = Number(updatedSkuObj?.Expected || 0);

      let updatedAccepted = [...acceptedSkus];

      if (remainingExpected === 0) {
        if (!updatedAccepted.includes(sku)) {
          updatedAccepted.push(sku);
          setAcceptedSkus(updatedAccepted);
        }
        alert(`✅ SKU Fully Accepted\n\nASN: ${asn}\nSKU: ${sku}`);
      } else {
        alert(`⚠️ Partial Receipt Saved\n\nASN: ${asn}\nSKU: ${sku}\nRemaining Qty: ${remainingExpected}`);
      }

      resetSkuLevel();

      const fullyAcceptedCount = updatedAccepted.length;
      const totalSkus = newSkuList.length;
      const remainingSkus = totalSkus - fullyAcceptedCount;

      if (remainingSkus === 0) {
        setOrderStatus("All SKUs Accepted");
        alert("✅ All SKUs fully accepted");
        setTimeout(() => {
          resetAll();
          document.getElementById("containerInput")?.focus();
        }, 1200);
      } else {
        setOrderStatus(`${remainingSkus} SKU(s) Pending`);
      }
    } catch (err) {
      console.error(err);
      alert("❌ Acceptance failed");
    }
  };

  /* ===============================
     REPORT DAMAGE / SHORT
  =============================== */
  const handleReport = async () => {
    const selectedSkuObj = skuList.find((s) => s.SKU === sku);
    if (!selectedSkuObj?.SKU) return alert("❌ Invalid SKU selected");
    if (!sku) return alert("⚠️ Select SKU");
    if (damaged <= 0) return alert("⚠️ Qty must be > 0");
    if (damaged > expected) return alert("❌ Damaged/Short cannot be > Expected");

    const payload = {
      asnNumber: asn,
      sku: selectedSkuObj.SKU,
      damagedPieces: damaged,
      shipperId,
      shipperName,
      productdescription: selectedSkuObj.ProductDescription || null,
      status: damageStatus,
    };

    try {
      await reportDamagedSku(payload);

      // Deduct locally and update recoverable qty locally
      setSkuList((prev) =>
        prev.map((item) =>
          item.SKU === sku
            ? { 
                ...item, 
                Expected: Math.max(Number(item.Expected) - Number(damaged), 0),
                RecoverableQty: (Number(item.RecoverableQty) || 0) + Number(damaged)
              }
            : item
        )
      );

      alert(`✅ ${damageStatus} Reported\nASN: ${asn}\nSKU: ${sku}\nQty: ${damaged}`);
      resetSkuLevel();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to report damaged/short");
    }
  };

  /* ===============================
     RECOVER DAMAGE / SHORT
  =============================== */
  const handleRecover = async () => {
    const selectedSkuObj = skuList.find((s) => s.SKU === sku);
    if (!selectedSkuObj?.SKU) return alert("❌ Invalid SKU selected");
    if (!sku) return alert("⚠️ Select SKU");
    if (recoveredQty <= 0) return alert("⚠️ Recovered qty must be > 0");
    if (recoveredQty > pendingRecovery) return alert("❌ Recovered qty cannot exceed pending quantity");

    try {
      const payload = {
        asnNumber: asn,
        sku: selectedSkuObj.SKU,
        recoveredPieces: recoveredQty,
        userId: JSON.parse(localStorage.getItem("user"))?.userId,
      };

      await recoverShortSku(payload);

      setSkuList((prev) =>
        prev.map((item) =>
          item.SKU === sku
            ? { 
                ...item, 
                Expected: Number(item.Expected) + Number(recoveredQty),
                RecoverableQty: Math.max(Number(item.RecoverableQty) - Number(recoveredQty), 0)
              }
            : item
        )
      );

      alert(`✅ Recovery Saved\nASN: ${asn}\nSKU: ${sku}\nQty: ${recoveredQty}`);
      resetSkuLevel();
    } catch (err) {
      console.error(err);
      alert("❌ Recovery failed");
    }
  };

  return (
    <div className="page">
      <h2 className="title">Fulfilment Center Acceptance</h2>

      <div className="card">
        <div className="row">
          <label>
            <input
              type="checkbox"
              checked={manual}
              onChange={() => {
                setManual(!manual);
                resetAll();
              }}
            />{" "}
            Manual Selection
          </label>

          <label>
            <input
              type="checkbox"
              checked={reportMode}
              onChange={() => {
                setReportMode(!reportMode);
                setRecoverMode(false);
                resetSkuLevel();
              }}
            />{" "}
            Report Damaged / Short-Received
          </label>

          <label>
            <input
              type="checkbox"
              checked={recoverMode}
              onChange={() => {
                setRecoverMode(!recoverMode);
                setReportMode(false);
                resetSkuLevel();
              }}
            />{" "}
            Recover Short/Damaged Qty
          </label>
        </div>

        <div className="input-group">
          <label>Container Number</label>
          <input
            id="containerInput"
            value={containerNo}
            onChange={(e) => setContainerNo(e.target.value)}
            onKeyDown={(e) => manual && e.key === "Enter" && loadDetails(containerNo)}
            autoFocus
          />
        </div>

        {manual && (
          <button className="btn secondary" onClick={() => loadDetails(containerNo)}>
            Load Details
          </button>
        )}

        <div className="input-group">
          <label>ASN</label>
          <input readOnly value={asn} />
        </div>

        <div className="input-group">
          <label>SKU</label>
          <select
            value={sku}
            onChange={(e) => {
              const selectedSku = e.target.value;
              const s = skuList.find((x) => x.SKU === selectedSku);

              setSku(selectedSku);
              setExpected(Number(s?.Expected || 0));
              setPendingRecovery(Number(s?.RecoverableQty || 0)); // ✅ Map backend field
              setReceived(0);
              setDamaged(0);
              setRecoveredQty(0);
            }}
          >
            <option value="">-- Select SKU --</option>
            {skuList.map((s) => (
              <option key={s.SKU} value={s.SKU}>
                {s.SKU}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label>Expected</label>
          <input readOnly value={expected} />
        </div>

        {/* RECOVER MODE - DISPLAY PENDING QTY */}
        {recoverMode && (
          <div className="input-group">
            <label>Pending to be Recovered</label>
            <input 
              readOnly 
              value={pendingRecovery} 
              style={{ backgroundColor: "#f0f0f0", fontWeight: "bold", color: "#E30016" }} 
            />
          </div>
        )}

        {/* ACCEPT MODE */}
        {!reportMode && !recoverMode && (
          <>
            <div className="input-group">
              <label>Received</label>
              <input
                type="number"
                value={received === 0 ? "" : received}
                onChange={(e) =>
                  setReceived(e.target.value === "" ? 0 : Number(e.target.value))
                }
              />
            </div>
            <div className="input-group">
              <label>Difference</label>
              <input readOnly value={difference} />
            </div>
          </>
        )}

        {/* REPORT MODE */}
        {reportMode && (
          <>
            <div className="input-group">
              <label>Damaged / Short Qty</label>
              <input
                type="number"
                value={damaged === 0 ? "" : damaged}
                onChange={(e) =>
                  setDamaged(e.target.value === "" ? 0 : parseInt(e.target.value, 10))
                }
              />
            </div>
            <div className="input-group">
              <label>Status</label>
              <select value={damageStatus} onChange={(e) => setDamageStatus(e.target.value)}>
                <option value="Damaged">Damaged</option>
                <option value="Short-Received">Short-Received</option>
              </select>
            </div>
          </>
        )}

        {/* RECOVER MODE INPUT */}
        {recoverMode && (
          <div className="input-group">
            <label>Recovered Qty</label>
            <input
              type="number"
              value={recoveredQty === 0 ? "" : recoveredQty}
              onChange={(e) =>
                setRecoveredQty(e.target.value === "" ? 0 : parseInt(e.target.value, 10))
              }
            />
          </div>
        )}

        <div className="input-group">
          <label>Acceptance Status</label>
          <input readOnly value={`${acceptedSkus.length} / ${skuList.length} Fully Accepted`} />
        </div>

        <div className="btn-row">
          {reportMode ? (
            <button className="btn secondary" onClick={handleReport}>
              Report
            </button>
          ) : recoverMode ? (
            <button className="btn secondary" onClick={handleRecover}>
              Recover
            </button>
          ) : (
            <button className="btn primary" onClick={handleAccept}>
              Accept
            </button>
          )}
        </div>

        <div style={{ marginTop: 10, fontWeight: "bold", color: "#444" }}>
          {orderStatus}
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
.page { padding:40px; background:#f8f8f8; min-height:100vh; }
.title { text-align:center; font-size:24px; margin-bottom:12px; color:#E30016; }
.card { background:#fff; padding:28px; border-radius:12px; max-width:520px; margin:auto; box-shadow:0 2px 6px rgba(0,0,0,0.1); }
.row { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
.input-group { margin-bottom:12px; width:95%; }
.input-group label { display: block; margin-bottom: 5px; font-weight: 500; }
.input-group input, .input-group select { width:99%; padding:10px; border-radius:6px; border:1px solid #777; }
.btn-row { display:flex; justify-content:space-between; gap:10px; }
.btn { padding:12px 20px; border:none; border-radius:8px; font-weight:bold; cursor:pointer; width:48%; }
.primary { background:#E30016; color:#fff; }
.secondary { background:#6E6F70; color:#fff; }
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
`;